import { toNumber } from '@/lib/cube-report'

type Row = Record<string, unknown>

export interface PivotGroup {
  /** Üst düzeylerle birlikte benzersiz kimlik (açık/kapalı durumu için). */
  id: string
  depth: number
  /** Bu düzeyin üyesi (`BankaBakiyeleri.firma`) ve ilk satırı (etiket, logo, döviz buradan). */
  member: string
  first: Row
  /** Ölçü -> alt toplam. Grupta birden çok döviz varsa `null` (TL ile USD toplanmaz). */
  totals: Record<string, number | null>
  /** Alt gruplar ya da (son düzeyde) yaprak satırlar. */
  children: PivotGroup[] | Row[]
}

function isGroupList(children: PivotGroup[] | Row[]): children is PivotGroup[] {
  return children.length > 0 && 'depth' in children[0] && 'children' in children[0]
}

/**
 * Satırları `levels` sırasıyla iç içe gruplar (Power BI matrisindeki gibi); alt
 * toplamlar yaprakların toplamıdır, yani yalnızca toplanabilir (`sum`) ölçüler
 * için doğrudur. Gruplar, `sortBy` verilirse o ölçünün alt toplamına göre, yoksa
 * gelen satır sırasıyla (sorgunun `order`'ı) dizilir.
 */
export function buildPivot(
  rows: Row[],
  levels: string[],
  measures: string[],
  currencyMember?: string,
  sortBy?: { member: string; desc: boolean },
  depth = 0,
  parentId = '',
): PivotGroup[] {
  const member = levels[depth]
  const buckets = new Map<string, Row[]>()
  for (const row of rows) {
    const key = String(row[member] ?? '')
    const bucket = buckets.get(key)
    if (bucket) bucket.push(row)
    else buckets.set(key, [row])
  }

  const groups = [...buckets].map(([key, items]): PivotGroup => {
    const id = `${parentId}/${key}`
    const mixed =
      currencyMember !== undefined && new Set(items.map((row) => row[currencyMember])).size > 1
    const totals = Object.fromEntries(
      measures.map((measure) => [
        measure,
        mixed ? null : items.reduce((sum, row) => sum + (toNumber(row[measure]) ?? 0), 0),
      ]),
    )
    const children =
      depth + 1 < levels.length
        ? buildPivot(items, levels, measures, currencyMember, sortBy, depth + 1, id)
        : items
    return { id, depth, member, first: items[0], totals, children }
  })

  if (sortBy) {
    const sign = sortBy.desc ? -1 : 1
    groups.sort((a, b) => sign * ((a.totals[sortBy.member] ?? 0) - (b.totals[sortBy.member] ?? 0)))
  }
  return groups
}

export type PivotLine =
  { kind: 'group'; group: PivotGroup } | { kind: 'leaf'; row: Row; depth: number }

/** Açık grupların altını düzleştirir (tabloya satır satır basmak için). */
export function flattenPivot(groups: PivotGroup[], expanded: ReadonlySet<string>): PivotLine[] {
  const lines: PivotLine[] = []
  for (const group of groups) {
    lines.push({ kind: 'group', group })
    if (!expanded.has(group.id)) continue
    if (isGroupList(group.children)) lines.push(...flattenPivot(group.children, expanded))
    else for (const row of group.children) lines.push({ kind: 'leaf', row, depth: group.depth + 1 })
  }
  return lines
}

/** Kolon boyutu (`c`) en fazla bu kadar değer alır; fazlası ilk ölçüye göre kesilir. */
export const PIVOT_COLUMN_LIMIT = 20

export interface PivotColumn {
  /** Geniş satırdaki anahtar (`columnKey`). */
  key: string
  measure: string
  /** Kolon boyutunun değeri; kolon boyutu yoksa `undefined`. */
  value?: string
}

export const columnKey = (measure: string, value?: string) =>
  value === undefined ? measure : `${measure}\u0000${value}`

/**
 * Kolon boyutunu (`colDim`) istemcide açar: her (satır anahtarı) için tek geniş satır,
 * her (değer × ölçü) için bir kolon. `keyDims` satır kimliğini belirler (satır düzeyleri
 * + döviz gibi ek boyutlar). Değer sayısı `limit`'i aşarsa ilk ölçünün toplamına göre
 * en büyük `limit` değer kalır.
 */
export function pivotColumns(
  rows: Row[],
  keyDims: string[],
  colDim: string | undefined,
  measures: string[],
  limit = PIVOT_COLUMN_LIMIT,
): { rows: Row[]; columns: PivotColumn[]; truncated: boolean } {
  if (!colDim) {
    return {
      rows,
      columns: measures.map((measure) => ({ key: measure, measure })),
      truncated: false,
    }
  }
  const valueOf = (row: Row) => String(row[colDim] ?? '')
  const weight = new Map<string, number>()
  for (const row of rows) {
    const value = valueOf(row)
    weight.set(value, (weight.get(value) ?? 0) + (toNumber(row[measures[0]]) ?? 0))
  }
  const ranked = [...weight].sort((a, b) => b[1] - a[1]).map(([value]) => value)
  const chosen = ranked.slice(0, limit).sort((a, b) => a.localeCompare(b, 'tr'))
  const allowed = new Set(chosen)
  const columns = chosen.flatMap((value) =>
    measures.map((measure) => ({ key: columnKey(measure, value), measure, value })),
  )

  const wide = new Map<string, Row>()
  for (const row of rows) {
    const value = valueOf(row)
    if (!allowed.has(value)) continue
    const id = JSON.stringify(keyDims.map((dim) => row[dim] ?? null))
    let target = wide.get(id)
    if (!target) {
      target = Object.fromEntries(keyDims.map((dim) => [dim, row[dim]]))
      wide.set(id, target)
    }
    for (const measure of measures) target[columnKey(measure, value)] = row[measure]
  }
  return { rows: [...wide.values()], columns, truncated: ranked.length > limit }
}
